import { CardFraming, HeroFraming } from "@/components/brand/Angels";
import { ReportsQueue } from "@/components/admin/ReportsQueue";
import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { Avatar, EmptyState, ErrorState, Spinner } from "@/components/common";
import { api, apiError, TYPE_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ApplicationDetails, CallSlotPanel, ScreeningPanel, STAGES } from "@/components/admin/Screening";
import { BookingLinkSettings } from "@/components/admin/BookingLink";
import { AdminStats, MemberAdminTools } from "@/components/admin/MemberAdmin";
import { RemoveMemberTools } from "@/components/admin/RemoveMember";

const TABS = ["pending", "approved", "rejected", "suspended", "removed"];
const TRACK_FILTERS = [["", "All"], ["woman", "Women"], ["man", "Men"]];
const TRACK_LABEL = { woman: "Woman", man: "Man" };
const STAGE_FILTERS = [["", "All stages"], ...Object.entries(STAGES)];
const INTENT_LABELS = { find_cohosts: "Find co-hosts", create_experiences: "Create experiences", networking: "Networking", dating: "Dating" };

const ApplicationCard = ({ a, onDecide }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const inviteScreening = async () => {
    setBusy("screening");
    try { await api.post(`/admin/applications/${a.id}/invite-screening`); toast.success(`Screening invite emailed to ${a.name}`); onDecide(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const decide = async (decision) => {
    if (decision === "rejected" && !window.confirm(`Reject ${a.name}'s application?`)) return;
    setBusy(decision);
    try { await api.post(`/admin/applications/${a.id}/decision`, { decision, note }); toast.success(`${a.name} ${decision}`); onDecide(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <li className="surface space-y-4 p-5 sm:p-6" data-testid={`application-${a.id}`}>
      <div className="flex flex-wrap items-center gap-4">
        <Avatar person={a} size={56} />
        <div className="min-w-0 flex-1">
          <p className="font-display text-2xl">{a.name} {a.application?.track && <span className="ml-2 align-middle rounded-md bg-secondary px-2 py-0.5 font-sans text-xs" data-testid={`application-track-${a.id}`}>{TRACK_LABEL[a.application.track]}</span>}
            {a.screening?.stage && <span className="ml-2 align-middle rounded-md bg-primary/10 px-2 py-0.5 font-sans text-xs text-primary" data-testid={`application-stage-${a.id}`}>{STAGES[a.screening.stage]}</span>}</p>
          <p className="truncate text-sm text-muted-foreground">{a.email} · {a.home_city} · applied {new Date(a.applied_at).toLocaleDateString("en-GB")}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-sm">
        {a.intents.map((i) => <span key={i} className="rounded-md bg-primary/10 px-3 py-1 text-primary">{INTENT_LABELS[i]}</span>)}
        {a.interests.map((i) => <span key={i} className="rounded-md bg-secondary px-3 py-1">{TYPE_LABELS[i] || i}</span>)}
      </div>
      {a.bio && <p className="whitespace-pre-line text-foreground/85">{a.bio}</p>}
      <ApplicationDetails a={a} />
      {a.review_note && <p className="text-sm text-muted-foreground">Note: {a.review_note}</p>}
      <MemberAdminTools a={a} onChange={onDecide} />
      {a.role !== "admin" && a.status !== "removed" && <ScreeningPanel key={`${a.id}-${a.screening?.stage}`} a={a} onSaved={onDecide} />}
      {a.role !== "admin" && a.status === "pending" && (
        <div className="flex flex-col gap-2 rounded-lg border border-primary/30 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`screening-invite-${a.id}`}>
          <div>
            <p className="font-semibold">Screening call</p>
            <p className="text-sm text-muted-foreground" data-testid={`screening-invite-status-${a.id}`}>
              {a.screening?.invited_at ? `Booking link emailed ${new Date(a.screening.invited_at).toLocaleDateString("en-GB")}. Mark them Call scheduled once they book.` : "Email them your booking link and mark them Shortlisted."}
            </p>
          </div>
          <button type="button" className="btn-primary shrink-0" disabled={!!busy} onClick={inviteScreening} data-testid={`approve-screening-${a.id}`}>
            {a.screening?.invited_at ? "Resend booking link" : "Approve for screening"}
          </button>
        </div>
      )}
      {a.role !== "admin" && a.status === "pending" && (
        <details className="group" data-testid={`one-off-call-${a.id}`}>
          <summary className="cursor-pointer text-sm text-primary underline-offset-4 hover:underline">Or send a specific call time instead</summary>
          <div className="mt-3"><CallSlotPanel a={a} onChange={onDecide} /></div>
        </details>
      )}
      {a.role !== "admin" && a.status !== "removed" && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input className="field flex-1" placeholder="Optional note (shown to the applicant if rejected)" maxLength={400} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Review note" data-testid={`review-note-${a.id}`} />
          {a.status !== "approved" && <button type="button" className="btn-primary" disabled={!!busy} onClick={() => decide("approved")} data-testid={`approve-${a.id}`}><Check className="h-4 w-4" /> Approve membership</button>}
          {a.status !== "rejected" && a.role !== "admin" && <button type="button" className="btn-outline" disabled={!!busy} onClick={() => decide("rejected")} data-testid={`reject-${a.id}`}><X className="h-4 w-4" /> {a.status === "approved" ? "Revoke" : "Reject"}</button>}
        </div>
      )}
      <RemoveMemberTools a={a} onChange={onDecide} />
    </li>
  );
};

export default function Admin() {
  const [tab, setTab] = useState("pending");
  const [track, setTrack] = useState("");
  const [stage, setStage] = useState("");
  const [list, setList] = useState(null);
  const [stats, setStats] = useState({});
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setList(null);
    try {
      const [a, s] = await Promise.all([api.get("/admin/applications", { params: { status: tab, ...(track ? { track } : {}), ...(stage ? { stage } : {}) } }), api.get("/admin/stats")]);
      setList(a.data); setStats(s.data); setError("");
    } catch (e) { setError(apiError(e)); }
  }, [tab, track, stage]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="shell max-w-[1000px] pb-24 pt-10" data-testid="admin-page">
      <p className="eyebrow mb-3">Owner tools</p>
      <h1 className="font-display text-5xl leading-none">Membership applications</h1>
      <p className="mt-3 text-muted-foreground">Every application is privately screened. Ratings and notes are visible to admins only.</p>
      <AdminStats refreshKey={list} />
      <BookingLinkSettings />
      <HeroFraming />
      <CardFraming />
      <ReportsQueue />
      <h2 className="font-display mt-12 text-3xl">Applications</h2>
      <div className="mt-8 flex gap-2" role="tablist">
        {TABS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={cn("chip capitalize")} data-active={tab === t}>
            {t} {stats[t] != null && <span className="ml-2 text-muted-foreground">{stats[t]}</span>}
          </button>
        ))}
      </div>
      <div className="mt-4 flex gap-2" role="group" aria-label="Filter by track">
        {TRACK_FILTERS.map(([id, label]) => (
          <button key={label} type="button" className="chip" data-active={track === id} aria-pressed={track === id} onClick={() => setTrack(id)} data-testid={`admin-track-${id || "all"}`}>{label}</button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Filter by screening status">
        {STAGE_FILTERS.map(([id, label]) => (
          <button key={label} type="button" className="chip" data-active={stage === id} aria-pressed={stage === id} onClick={() => setStage(id)} data-testid={`admin-stage-${id || "all"}`}>{label}</button>
        ))}
      </div>
      <div className="mt-8">
        {error ? <ErrorState message={error} onRetry={load} /> : !list ? <Spinner /> : list.length === 0 ? <EmptyState title={`No ${tab} applications.`} testId="admin-empty" /> : (
          <ul className="space-y-4" data-testid="application-list">{list.map((a) => <ApplicationCard key={a.id} a={a} onDecide={load} />)}</ul>
        )}
      </div>
    </div>
  );
}
